import axios, {
    AxiosError,
    AxiosInstance,
    AxiosResponse,
    CreateAxiosDefaults,
    InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import qs from 'qs';
import { v4 as uuid } from 'uuid';

import { Response } from '@/infra/adapters/models';
import { deserialize } from '@/infra/adapters/transformer';
import { CONFIG } from '@/infra/config';
import {
    AuthenticationError,
    DuplicateRecordError,
    ForbiddenError,
    InvalidParamError,
    NotFoundError,
    ServerError,
} from '@/infra/exceptions';

import { StatusCode } from './interface';

const requestOption: CreateAxiosDefaults = {
    baseURL: CONFIG.HTTP_BASE,
    timeout: 30000, // 30s
    headers: {
        'X-Request-WITH': 'XMLHttpRequest',
    },
    validateStatus: (status) => status >= 200 && status < 300,
    paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
};

const retryCondition = (err: AxiosError) => {
    const status = err?.response?.status ?? 0;
    if (status >= 500 && status < 600) return true;
    return false;
};

export const retryOption: IAxiosRetryConfig = {
    retries: 3,
    retryDelay: (retryCount: number) => retryCount * 1000,
    retryCondition,
};

const apiv1: AxiosInstance = axios.create({
    ...requestOption,
    baseURL: requestOption.baseURL + '/api/v1',
});

apiv1.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        config.headers['X-Request-ID'] = uuid();
        return config;
    },
    (err: unknown) => {
        return Promise.reject(err);
    }
);

apiv1.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (err: AxiosError) => {
        console.error('AxiosError', err);
        if (err.response) {
            const { data, status } = err.response;
            if (
                status === StatusCode.BAD_REQUEST ||
                status === StatusCode.UNPROCESSABLE_ENTITY
            ) {
                const resp = deserialize(Response<string>, data);
                err.cause = new InvalidParamError(resp);
            } else if (status === StatusCode.CONFLICT) {
                const resp = deserialize(Response, data);
                err.cause = new DuplicateRecordError(resp);
            } else if (status === StatusCode.UNAUTHORIZED) {
                const resp = deserialize(Response, data);
                err.cause = new AuthenticationError(resp);
            } else if (status === StatusCode.NOT_FOUND) {
                const resp = deserialize(Response<string>, data);
                err.cause = new NotFoundError(resp);
            } else if (status === StatusCode.FORBIDDEN) {
                const resp = deserialize(Response<string>, data);
                err.cause = new ForbiddenError(resp);
            } else {
                err.cause = new ServerError(err.message);
            }
        } else if (err.request) {
            console.error('AxiosRequestError', err.request);
        } else {
            console.error('Error', err.message);
        }
        return Promise.reject(err);
    }
);
axiosRetry(apiv1, retryOption);

export { apiv1 };
